import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'outline'
  | 'orange-subtle'
  | 'link';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-xs' | 'icon-sm';

export interface BaseButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
}

export type ButtonAsButton = BaseButtonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> & {
    href?: undefined;
    to?: undefined;
  };

export type ButtonAsAnchor = BaseButtonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps> & {
    href: string;
    to?: undefined;
  };

export type ButtonAsLink = BaseButtonProps & {
  to: string;
  href?: undefined;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  target?: string;
  rel?: string;
};

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsLink;

export const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      isLoading = false,
      loading = false,
      leftIcon,
      icon,
      rightIcon,
      fullWidth = false,
      children,
      className = '',
      disabled = false,
      ...props
    },
    ref
  ) => {
    const effectiveLoading = isLoading || loading;
    const effectiveLeftIcon = leftIcon || icon;
    const variantStyles: Record<ButtonVariant, string> = {
      primary:
        'bg-cf-orange hover:bg-cf-orangeHover text-cf-accentText font-semibold shadow-xs focus-visible:ring-2 focus-visible:ring-cf-orange/50',
      secondary:
        'bg-cf-bg hover:bg-cf-surfaceHover text-cf-textPrimary border border-cf-border hover:border-cf-textMuted focus-visible:ring-2 focus-visible:ring-cf-border',
      ghost:
        'bg-transparent hover:bg-cf-surfaceHover text-cf-textSecondary hover:text-cf-textPrimary',
      danger:
        'bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/30 focus-visible:ring-2 focus-visible:ring-rose-500/50',
      outline:
        'bg-transparent hover:bg-cf-surface border border-cf-border text-cf-textPrimary hover:border-cf-textMuted',
      'orange-subtle':
        'bg-cf-orangeMuted hover:bg-cf-orange/20 border border-cf-orange/30 text-cf-orange font-medium',
      link:
        'bg-transparent text-cf-orange hover:underline p-0 h-auto font-normal focus-visible:ring-0',
    };

    const sizeStyles: Record<ButtonSize, string> = {
      xs: 'text-xs px-2.5 py-1 rounded gap-1.5',
      sm: 'text-xs px-3 py-1.5 rounded-md gap-1.5 font-medium',
      md: 'text-xs px-3.5 py-2 rounded-md gap-2 font-medium',
      lg: 'text-sm px-4 py-2.5 rounded-md gap-2 font-medium',
      icon: 'p-2 rounded-md gap-0',
      'icon-sm': 'p-1.5 rounded-md gap-0',
      'icon-xs': 'p-1 rounded gap-0',
    };

    const spinnerSize =
      size === 'xs' || size === 'icon-xs' || size === 'icon-sm'
        ? 'w-2.5 h-2.5'
        : 'w-3.5 h-3.5';

    const buttonClasses = cn(
      'inline-flex items-center justify-center whitespace-nowrap transition-colors cursor-pointer select-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      variantStyles[variant],
      variant !== 'link' && sizeStyles[size],
      fullWidth && 'w-full',
      className
    );

    const content = (
      <>
        {effectiveLoading ? (
          <span
            className={cn(
              spinnerSize,
              'border-2 border-current border-t-transparent rounded-full animate-spin shrink-0'
            )}
          />
        ) : (
          effectiveLeftIcon && (
            <span className="shrink-0 inline-flex items-center">{effectiveLeftIcon}</span>
          )
        )}
        {children}
        {!effectiveLoading && rightIcon && (
          <span className="shrink-0 inline-flex items-center">{rightIcon}</span>
        )}
      </>
    );

    const isDisabled = disabled || effectiveLoading;

    if ('to' in props && props.to) {
      const { to, onClick, target, rel } = props as ButtonAsLink;
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          onClick={isDisabled ? (e) => e.preventDefault() : onClick}
          target={target}
          rel={rel}
          className={cn(buttonClasses, isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none')}
          aria-disabled={isDisabled}
        >
          {content}
        </Link>
      );
    }

    if ('href' in props && props.href) {
      const { href, onClick, target, rel, ...anchorProps } = props as ButtonAsAnchor;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          onClick={isDisabled ? (e) => e.preventDefault() : onClick}
          target={target}
          rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)}
          className={cn(buttonClasses, isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none')}
          aria-disabled={isDisabled}
          {...anchorProps}
        >
          {content}
        </a>
      );
    }

    const { type = 'button', ...buttonProps } = props as ButtonAsButton;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        disabled={isDisabled}
        className={buttonClasses}
        {...buttonProps}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';
